import { ImmutableState, DeclaredState, FitnessState } from './data-model/user-state';
import { FitnessPlan } from './data-model/fitness-plan';
import { Schedule_Day } from './data-model/schedule';
import { Log_WeighIn } from './data-model/log-weigh-in';
import { Log_Entry } from './data-model/user-log';
import { AdjustmentAction, AdjustmentActionReason, BodyFatSeries, DatedSlopePoint } from './plan-update-internals';

/**
 * How many days back the caller of `checkForPlanAdjustments` gathers the
 * `weighIns` and `historyLogs` it passes in.
 *
 * The solver does not read this itself — it judges whatever entries it is given —
 * so it is the CALLERS that have to agree on the span, and there are two of them:
 * the user's own app (`SvPlanHandlerService.checkForPlanAdjustments`) and the
 * coach dashboard, which recomputes the same status for somebody else's account.
 * They must gather the same days or the coach is shown a status the client's own
 * app would never produce, on a screen that looks perfectly healthy.
 *
 * It lives here so that is one number rather than a copy in each caller kept true
 * by a comment asking a person to remember the other one.
 */
export const PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS = 14;

export interface PlanUpdateParams {
  immutableState: ImmutableState;
  declaredState: DeclaredState;
  fitnessState: FitnessState;
  fitnessPlan: FitnessPlan;
  schedule?: Schedule_Day[];
  weighIns: Log_WeighIn[];
  historyLogs: Log_Entry[];
  today_local: string;
  testing?: boolean /** set for unit tests */;
}

export interface PlanUpdateInternals {
  // details are private to the server
}

export enum PlanUpdateUserMessage {
  /** Nothing to report to the user. Either data is good, or just starting so no data is expected. */
  AllOk = 'AllOk',
  /** There was not enough data to calculate a proper solution, or no data at all. */
  NotEnoughData = 'NotEnoughData',
  /** There was barely enough data, but it was scarce in the number of samples. */
  MeasureMoreOften = 'MeasureMoreOften',
  /** There was data, but it's all over the place. Tell the user to increase orderliness. */
  DataErratic = 'DataErratic',
  /** Weight data is suspiciously stable. Tell the user to check the scale. */
  WeightTooFlat = 'WeightTooFlat',
  /** No bodyweight available from any source — recent weighins, plan baseline, or
   *  fitnessState. The plan can't be fully recalculated; ask the user to log a
   *  current weigh-in. (#3286 safety net.) */
  LogWeighInNeeded = 'LogWeighInNeeded',
  /** A cutting branch in `findAdjustmentAction` would have decreased intake on an
   *  "unclear" fatness slope, but the underlying sample is below the minimum that
   *  makes the p30/p70 gate reliable (<5 caliper AND <7 tape in the 14-day window).
   *  The adjustment is deferred; ask the user to log a few more measurements.
   *  See `design-docs/body-comp-measurement-cadence.md` and #2855. */
  InsufficientFatnessConfidence = 'InsufficientFatnessConfidence',
}

/**
 * Which kind of answer a `PlanUpdate` method gave. A string enum and not a boolean on purpose:
 * this repository compiles with `strictNullChecks` off, and under that setting TypeScript does
 * not narrow a union by a boolean field, so a boolean would not make the failed branch unreadable.
 */
export enum PlanUpdateOutcome {
  /** the method ran and produced an answer */
  Solved = 'solved',
  /** the method crashed, or produced numbers `deepCheckValid` refused; there is no answer to read */
  Failed = 'failed',
}

/**
 * The failed branch of every `PlanUpdate` result.
 *
 * The three result types used to be one flat object with `error: string; // empty if all is ok`
 * beside the answer fields. A caller that forgot to read `error` compiled, and read our own
 * arithmetic failing as an ordinary answer -- the same trap #5297 hit on the meal tuner. Now each
 * type is a union on `outcome`, and this branch carries no answer fields, so a caller cannot reach
 * one without asking first. The shape copies `MealTunerResults` (#5776).
 *
 * `PlanUpdate` answers a failure rather than throwing it, and the caller decides whether to throw:
 * `SvPlanHandlerService` does, others report and carry on. That split is the one decided for the
 * meal tuner in #5776, whose reason is on `MealTunerResults`.
 */
export interface PlanUpdateFailed {
  outcome: PlanUpdateOutcome.Failed;
  warning: string;
  /** never empty: every catch in `PlanUpdate` substitutes its own wording for a blank description */
  error: string;
  internals?: PlanUpdateInternals /** only if testing is true */;
}

export interface PlanUpdateSolved {
  outcome: PlanUpdateOutcome.Solved;
  warning: string;
  message: PlanUpdateUserMessage;
  /** Same as the one from input, exercises are updated as needed for periodization. */
  fitnessPlan: FitnessPlan;
  /** Set when adjustForProgress found a stored energy balance whose sign
   *  contradicts the goal direction (e.g. a cutting-era deficit carried into a
   *  muscle-growth program) and substituted the goal-consistent default delta
   *  instead of failing the solve. Both values are fractions (0.045 = +4.5%).
   *  The caller must surface this to telemetry — shared code has no Sentry
   *  runtime, so reporting happens client-side (#4037). */
  healedIntakeDelta?: {
    stored: number;
    used: number;
    /** the adjustment action that hit the inconsistency */
    action: AdjustmentAction;
  };
  /** Set when this solve decided to lower the daily target and the minimum daily intake
   *  raised the result back up, so the target now sits on the minimum. Kept apart from
   *  `message`, so a solve that also carries a data-quality message still reports it. Whether
   *  the resulting change is worth offering is the weekly checkup's decision (#5415). */
  clampedToMinimumIntake?: boolean;
  /** The lowest daily target this solve would set for the person's body weight and planned
   *  deficit, in kcal (#5456). Returned so a screen can warn when somebody types a target below
   *  it; the solve itself decides nothing from this field. Undefined when the nutrition targets
   *  were not worked out in this solve. */
  minimumDailyAverageIntake?: number;
  internals?: PlanUpdateInternals /** only if testing is true */;
}

export type PlanUpdateResults = PlanUpdateSolved | PlanUpdateFailed;

export interface GenerateScheduleSolved {
  outcome: PlanUpdateOutcome.Solved;
  warning: string;
  /** Same as the one from input (items are not moved), but nutrient targets are added to meals and days. */
  schedule: Schedule_Day[];
  internals?: PlanUpdateInternals /** only if testing is true */;
}

/**
 * `generateSchedule` failed. The schedule is still handed back, under a name that says what it
 * is, because one caller reads it on purpose: `generateBacklogInMemoryAsync` in
 * `user-db.service.ts`, for an account that has not finished the intake yet, where failing is the
 * normal state. Its targets were never computed -- a throw hands back the schedule passed IN, and
 * a validity failure hands back a half-built one carrying non-finite numbers. Read it only when
 * that is what you want.
 */
export interface GenerateScheduleFailed extends PlanUpdateFailed {
  unsolvedSchedule: Schedule_Day[];
}

export type GenerateScheduleResult = GenerateScheduleSolved | GenerateScheduleFailed;

export interface CheckForPlanAdjustmentsSolved {
  outcome: PlanUpdateOutcome.Solved;
  warning: string;
  message?: PlanUpdateUserMessage;
  needsAdjustment: boolean;
  /**
   * true when the progress check reached a verdict: either a change, or a deliberate "no change
   * needed". false when it could not judge progress at all (too few weigh-ins or body-fat
   * readings, no body-fat estimate yet, or the check was skipped). A verdict can still carry a
   * message other than AllOk, so read this field, not the message, to tell the two apart (#6067).
   */
  actionDecided: boolean;
  /**
   * true when the verdict depends on the body-fat trend and the readings in the window are too
   * few to judge it. The weigh-in page then asks for a skinfold or waist reading today, whatever
   * the user's measuring rhythm says (#6067).
   */
  needsMoreBodyFatReadings: boolean;
  adjustmentReason?: AdjustmentActionReason;
  /** Theil-Sen estimated weight (kg) 7 days ago on the trend line */
  estimatedBW_kg?: number;
  /**
   * #5456: numbers the check already worked out, handed back so a screen can show exactly what the
   * verdict was based on. All optional, all display only. Absent when the check had too little data.
   */
  /** the Theil-Sen weight slope, kg per week */
  weightSlope_kgwk?: number;
  /** the 30th and 70th percentile slopes of the body-fat series, per day */
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;
  /** which series those two slopes were read from */
  fatnessSeries?: BodyFatSeries;
  /** the readings the check fitted (at most 14 each, at most 14 days back, none older than the last calorie change) */
  weightSamples?: DatedSlopePoint[];
  caliperSamples?: DatedSlopePoint[];
  tapeSamples?: DatedSlopePoint[];
  /**
   * the calendar day of the earliest reading in whichever series the check fitted (the weight
   * series, and the fat series it chose). Undefined when it fitted nothing.
   */
  firstCountedDayIso?: string;
  /** Same as the one from input (items are not moved), but nutrient targets are added to meals and days. */
  internals?: PlanUpdateInternals /** only if testing is true */;
}

export type CheckForPlanAdjustmentsResult = CheckForPlanAdjustmentsSolved | PlanUpdateFailed;
